import { Box } from '@mui/material';
import { cashPay } from '../../../Constants';
import { useFormikContext } from 'formik';
import { InsuranceFormType } from '../hooks/use-insurance-form.hook';
import { UnsupportedInsuranceAlert } from './unsupported-insurance-alert.component';
import { useMemo } from 'react';
import { useL10n } from '@uplift/hooks';
import { InsuranceSection } from '../Settings/types';
import { InsuranceAutocomplete } from '../../../insurance-autocomplete';
import { TextInput } from '../../../Inputs';
import { useFormStyles } from '../form-styles';
import { CampusPlan, getProviderPublicProfile, getUserMatches } from '@uplift/api';
import { useQuery } from '@tanstack/react-query';
import { SubSystem } from '@uplift/types';
import { useGetStudentByStudentId, useHigherEdOrgQuery, useUserQuery } from '@uplift/queries';


export type InsuranceDetailsFormType = {
  isAddInsurance?: boolean;
  insuranceSection: InsuranceSection;
  patientId: string;
};

const useMatchesSupportedInsurances = (userId: string) => {
  return useQuery({
    queryKey: [ 'user', userId, 'matches-with-supported-insurances' ],
    queryFn: async () => {
      const { data: { matches } } = await getUserMatches({ userId, states: [ 'active' ] });

      return await Promise.all(matches.filter(match => !!match.providerId).map(async (match) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const providerId = match.providerId!;
        const { data: providerProfile } = await getProviderPublicProfile(providerId);

        return {
          providerId: providerId,
          supportedInsuranceCompanyIds: providerProfile.supportedInsuranceCompanies.map(c => c.id),
        };
      }));
    },
  });
};

const useIsSponsored = (userId: string) => {
  const { data: patient, isInitialLoading: patientLoading } = useUserQuery(userId);
  const { data: student, isInitialLoading: studentLoading } = useGetStudentByStudentId(userId, { enabled: patient?.subsystem === SubSystem.higherEd });
  const { data: org, isInitialLoading: orgLoading } = useHigherEdOrgQuery(student?.orgId ?? '', { enabled: !!student?.orgId });
  const contractType = org?.contract?.plan?.type;

  return {
    isSponsored: contractType && [ CampusPlan.flex, CampusPlan.complete ].includes(contractType),
    isLoading: patientLoading || studentLoading || orgLoading,
  };
};

export const InsuranceDetailsForm: React.VFC<InsuranceDetailsFormType> = ({ isAddInsurance = false, insuranceSection, patientId }) => {
  const l10n = useL10n();
  const { classes: formClasses } = useFormStyles();
  const formik = useFormikContext<InsuranceFormType>();
  const { data: matchesSupportedInsurances = [] } = useMatchesSupportedInsurances(patientId);
  const { isSponsored, isLoading: isSponsoredLoading } = useIsSponsored(patientId);

  const currentInsuranceId = formik.values.insuranceProvider;
  const matchesThatDontSupportInsurance = useMemo(() => {
    if (!currentInsuranceId || cashPay.id === currentInsuranceId) {
      return [];
    }

    if (isSponsoredLoading || isSponsored) {
      return [];
    }

    // filter out matches where the current insurance ID is not supported by the provider
    return matchesSupportedInsurances.filter((data) => !data.supportedInsuranceCompanyIds.includes(currentInsuranceId));
  }, [ currentInsuranceId, isSponsored, isSponsoredLoading, matchesSupportedInsurances ]);

  return (
    <div>
      {currentInsuranceId && !!matchesThatDontSupportInsurance.length && (
        <Box mb={3}>
          <UnsupportedInsuranceAlert
            unsupportedMatches={matchesThatDontSupportInsurance}
            currentInsuranceId={currentInsuranceId}
            insuranceSection={insuranceSection}
            patientId={patientId}
          />
        </Box>
      )}
      <Box className={formClasses.formGroup}>
        <InsuranceAutocomplete
          name="insuranceProvider"
          label={l10n.accountSettings.insurance.insuranceProvider}
          excludeCashPay={isAddInsurance}
        />
        <TextInput
          name="memberId"
          label={l10n.accountSettings.insurance.memberId}
          color="secondary"
        />
        <TextInput
          name="groupNumber"
          label={l10n.accountSettings.insurance.groupNumberOptional}
          color="secondary"
        />
      </Box>
    </div>
  );
};
